// Runs inside the room host's own browser tab. Ported from the old Node
// lobby server's connection/message handling (server/src/index.ts) so the
// host's tab now plays the role that process used to play: it owns the
// authoritative Room and relays state to every other player, who each hold
// a single PeerJS connection to the host only (star topology).
//
// The host is also a player in its own game. Its "connection" is just a
// direct in-process callback (`deliverLocal`) instead of a DataConnection —
// there's no network hop for the host talking to itself.

import type { DataConnection, Peer } from 'peerjs';
import { watchConnectionHealth } from './peer';
import { AVATAR_IDS, Room } from './rooms';
import type { ClientMessage, ServerMessage } from './protocol';

interface RemoteLink {
  reliable: DataConnection;
  unreliable: DataConnection | null;
  playerId: string | null;
}

export class HostSession {
  private readonly peer: Peer;
  private readonly room: Room;
  private readonly hostPlayerId: string;
  private readonly links = new Map<string, RemoteLink>();

  constructor(peer: Peer, roomCode: string, hostName: string, deliverLocal: (msg: ServerMessage) => void) {
    this.peer = peer;
    this.hostPlayerId = crypto.randomUUID();
    this.room = new Room(roomCode, {
      id: this.hostPlayerId,
      name: hostName,
      ready: false,
      avatarId: AVATAR_IDS[0],
      sendReliable: deliverLocal,
      sendUnreliable: deliverLocal,
    });
    deliverLocal({ type: 'room_joined', playerId: this.hostPlayerId, room: this.room.toState() });
    peer.on('connection', (conn) => this.handleIncomingConnection(conn));
  }

  // Routes a ClientMessage the host player generated locally (from
  // client.ts's send()) through the same handling remote players' messages
  // go through — except leave_room, which client.ts intercepts and turns
  // into destroy() instead, since the host leaving ends the whole session.
  handleLocal(msg: ClientMessage): void {
    this.handleMessage(this.hostPlayerId, msg);
  }

  private handleIncomingConnection(conn: DataConnection): void {
    const link = this.links.get(conn.peer) ?? { reliable: conn, unreliable: null, playerId: null };
    if (conn.label === 'ctrl') link.reliable = conn;
    else link.unreliable = conn;
    this.links.set(conn.peer, link);
    conn.on('data', (raw) => this.onRemoteData(link, raw as ClientMessage));
    if (conn.label === 'ctrl') watchConnectionHealth(conn, () => this.onRemoteClosed(conn.peer, link));
  }

  private onRemoteData(link: RemoteLink, msg: ClientMessage): void {
    if (link.playerId === null) {
      if (msg.type !== 'join_room') return; // ignore anything before the handshake
      const id = crypto.randomUUID();
      link.playerId = id;
      this.room.addPlayer({
        id,
        name: msg.name,
        ready: false,
        avatarId: this.room.assignNextAvatar(),
        sendReliable: (m) => { if (link.reliable.open) link.reliable.send(m); },
        sendUnreliable: (m) => { if (link.unreliable?.open) link.unreliable.send(m); },
      });
      link.reliable.send({ type: 'room_joined', playerId: id, room: this.room.toState() } satisfies ServerMessage);
      this.room.broadcast({ type: 'room_update', room: this.room.toState() } satisfies ServerMessage);
      return;
    }
    this.handleMessage(link.playerId, msg);
  }

  private onRemoteClosed(remotePeerId: string, link: RemoteLink): void {
    this.links.delete(remotePeerId);
    if (!link.playerId) return;
    this.room.removePlayer(link.playerId);
    this.room.broadcast({ type: 'room_update', room: this.room.toState() } satisfies ServerMessage);
  }

  private sendErrorTo(playerId: string, message: string): void {
    this.room.getPlayer(playerId)?.sendReliable({ type: 'error', message });
  }

  private handleMessage(playerId: string, msg: ClientMessage): void {
    switch (msg.type) {
      case 'set_avatar': {
        const result = this.room.setAvatar(playerId, msg.avatarId);
        if (result.success) this.room.broadcast({ type: 'room_update', room: this.room.toState() });
        else this.sendErrorTo(playerId, 'That fisherman is already taken.');
        break;
      }
      case 'set_ready': {
        this.room.setReady(playerId, msg.ready);
        this.room.broadcast({ type: 'room_update', room: this.room.toState() });
        break;
      }
      case 'update_settings': {
        if (this.room.hostId !== playerId) { this.sendErrorTo(playerId, 'only the host can change settings'); return; }
        this.room.updateSettings(msg.settings);
        this.room.broadcast({ type: 'room_update', room: this.room.toState() });
        break;
      }
      case 'start_game': {
        if (this.room.hostId !== playerId) { this.sendErrorTo(playerId, 'only the host can start the game'); return; }
        if (!this.room.allReady()) { this.sendErrorTo(playerId, 'not all players are ready'); return; }
        this.room.started = true;
        this.room.startClock();
        const state = this.room.toState();
        this.room.broadcast({ type: 'game_start', settings: state.settings, players: state.players });
        break;
      }
      case 'player_path_update': {
        const last = msg.waypoints[msg.waypoints.length - 1];
        if (last) this.room.updateTransform(playerId, { x: last.x, z: last.z, heading: last.heading }, last.boat, last.cart);
        this.room.broadcastPath({
          type: 'player_path_update', id: playerId, waypoints: msg.waypoints, gameTime: this.room.currentGameTime(),
        });
        break;
      }
      case 'jetty_deck_placed': {
        this.room.addJettyPlacement({ cells: msg.cells, angle: msg.angle });
        this.room.broadcast({ type: 'jetty_deck_placed', id: playerId, cells: msg.cells, angle: msg.angle });
        break;
      }
      case 'shelter_placed': {
        this.room.addShelterPlacement({ id: playerId, x: msg.x, z: msg.z, heading: msg.heading });
        this.room.broadcast({ type: 'shelter_placed', id: playerId, x: msg.x, z: msg.z, heading: msg.heading });
        break;
      }
      case 'score_update': {
        this.room.updateScore(playerId, msg.base);
        this.room.broadcast({ type: 'score_update', id: playerId, base: msg.base, stats: msg.stats });
        break;
      }
      case 'leave_room': {
        if (playerId === this.hostPlayerId) return; // handled by client.ts calling destroy() instead
        this.room.removePlayer(playerId);
        this.room.broadcast({ type: 'room_update', room: this.room.toState() });
        break;
      }
    }
  }

  destroy(): void {
    this.room.broadcast({ type: 'error', message: 'Host left — room closed.' });
    for (const link of this.links.values()) { link.reliable.close(); link.unreliable?.close(); }
    this.peer.destroy();
  }
}
